#!/usr/bin/env node
import { createAgent, isToolRequest, toolNamesForMessage } from '@vexalyn/agent';
import { createProvider, MockAIProvider } from '@vexalyn/ai';
import { loadConfig } from '@vexalyn/config';
import { createLogger } from '@vexalyn/logger';
import { createFilesystemToolRegistry, PermissionManager, PlanManager, listSkills, createTaskTool, getSubagentPrompt, todoReadTool, readFileTool, grepTool, globTool, getProjectInfoTool, getProjectStructureTool, getCodeOutlineTool, semanticSearchTool, gitStatusTool, gitDiffTool, lspTool, listDirectoryTool, questionTool, todoWriteTool, clearTodos } from '@vexalyn/tools';
import { generateRepositoryMap, formatCompactRepositoryMap } from '@vexalyn/context';
import { createInterface } from 'node:readline';
import { homedir } from 'node:os';
import { Transform } from 'node:stream';
import { fileURLToPath } from 'node:url';
import { resolve } from 'node:path';
import { writeAgentEventResponse, stopActiveLoader } from './output.js';
// createSpinner removed - opencode uses background toast instead of blocking spinner
import { existsSync, readFileSync, writeFileSync, mkdirSync, unlinkSync, readdirSync, statSync } from 'node:fs';
import { runModelCommand, runCompactCommand, runHistoryCommand, runHelpCommand, runPlanCommand, runUsageCommand, runFilePicker, hasConfiguredProvider, DEFAULT_PROVIDER_MODELS, PROVIDER_LABELS } from './commands.js';
import type { ProviderName } from './commands.js';
import { createTasksTool, completeTaskTool, globalTaskTracker } from './task-tracker.js';
import { loadEnvFiles } from './env-loader.js';

function initActiveProvider(config: ReturnType<typeof loadConfig>): ReturnType<typeof createProvider> {
  try {
    return process.env.VEXALYN_USE_MOCK === 'true' ? new MockAIProvider() : createProvider(config);
  } catch (error) {
    printProviderSetupError(
      config.defaultProvider,
      error instanceof Error ? error.message : String(error),
    );
    process.exit(1);
  }
}

function printProviderSetupError(providerName: string, reason: string): void {
  const PURPLE = '\x1b[38;2;168;85;247m';
  const RESET = '\x1b[0m';
  const RED = '\x1b[91m';
  const GREY = '\x1b[90m';
  const WHITE = '\x1b[97m';
  const WIDTH = 64;
  const visible = (text: string): number => text.replace(/\x1b\[[0-9;]*m/g, '').length;
  const row = (content: string): void => {
    console.log(`${PURPLE}│${RESET} ${content}${' '.repeat(Math.max(0, WIDTH - visible(content)))} ${PURPLE}│${RESET}`);
  };
  const edge = (left: string, right: string): void => {
    console.log(`${PURPLE}${left}${'─'.repeat(WIDTH + 2)}${right}${RESET}`);
  };
  console.log('');
  edge('╭', '╮');
  row(`${RED}◆ Gagal inisialisasi AI provider${RESET}`);
  row('');
  row(`${GREY}Provider${RESET}   ${WHITE}${(PROVIDER_LABELS as Record<string, string>)[providerName] ?? providerName}${RESET}`);
  row(`${GREY}Alasan${RESET}     ${reason}`);
  row('');
  row(`${GREY}Solusi${RESET}`);
  row(`  1. Isi API key provider di file ${WHITE}.env${RESET} pada folder workspace`);
  row(`  2. Atau pilih provider lain via ${WHITE}VEXALYN_DEFAULT_PROVIDER${RESET} di .env`);
  row(`  3. Atau jalankan mode tanpa API key: ${WHITE}VEXALYN_USE_MOCK=true${RESET}`);
  edge('╰', '╯');
console.log('');
}

async function runAuthCommand(): Promise<void> {
  const args = process.argv.slice(2);
  const authIndex = args.findIndex(a => a === 'auth');
  if (authIndex === -1) return;

  const subcommand = args[authIndex + 1] ?? 'help';
  
  const configDir = resolve(homedir(), '.vexalyn');
  const authFile = resolve(configDir, 'auth.json');

  async function loadAuth(): Promise<Record<string, { apiKey: string; baseUrl?: string }>> {
    if (!existsSync(authFile)) return {};
    try {
      return JSON.parse(readFileSync(authFile, 'utf8'));
    } catch {
      return {};
    }
  }

  async function saveAuth(auth: Record<string, { apiKey: string; baseUrl?: string }>): Promise<void> {
    if (!existsSync(configDir)) {
      mkdirSync(configDir, { recursive: true });
    }
    writeFileSync(authFile, JSON.stringify(auth, null, 2), 'utf8');
  }

  if (subcommand === 'login') {
    const provider = args[authIndex + 2] ?? '';
    if (!provider) {
      console.error('Usage: vexalyn auth login <provider>');
      console.error('Available: qwen, openai, gemini, anthropic, deepseek, groq, openrouter, ...');
      process.exit(1);
    }
    
    console.log(`\n🔐 Login ke ${provider}`);
    console.log('Masukkan API key (input akan tersembunyi):');
    
    // Use readline for password input
    const rl = createInterface({ input: process.stdin, output: process.stdout });
    process.stdout.write('\x1b[8m'); // Hide input
    const apiKey = await new Promise<string>((resolve) => {
      rl.question('API Key: ', (answer) => {
        process.stdout.write('\x1b[0m'); // Restore
        rl.close();
        resolve(answer.trim());
      });
    });
    
    if (!apiKey) {
      console.error('API key tidak boleh kosong');
      process.exit(1);
    }

    const auth = await loadAuth();
    auth[provider] = { apiKey };
    await saveAuth(auth);
    console.log(`\n✅ ${provider} API key disimpan ke ${authFile}`);
    console.log('Restart Vexalyn untuk memakai provider ini.');
  } else if (subcommand === 'list' || subcommand === 'ls') {
    const auth = await loadAuth();
    const providers = Object.keys(auth);
    if (providers.length === 0) {
      console.log('Tidak ada provider terautentikasi.');
    } else {
      console.log('\nProvider terautentikasi:');
      for (const p of providers) {
        console.log(`  ✓ ${p}`);
      }
    }
  } else if (subcommand === 'logout') {
    const provider = args[authIndex + 2] ?? '';
    if (!provider) {
      console.error('Usage: vexalyn auth logout <provider>');
      process.exit(1);
    }
    
    const auth = await loadAuth();
    if (auth[provider]) {
      delete auth[provider];
      await saveAuth(auth);
      console.log(`✅ ${provider} logged out`);
    } else {
      console.log(`${provider} tidak ditemukan`);
    }
  } else {
    console.log(`Usage: vexalyn auth <login|list|logout> [provider]`);
    console.log('  login <provider>  - Simpan API key untuk provider');
    console.log('  list              - Tampilkan provider tersimpan');
    console.log('  logout <provider> - Hapus provider');
  }
}

async function runMcpCommand(): Promise<void> {
  const args = process.argv.slice(2);
  const mcpIndex = args.findIndex(a => a === 'mcp');
  if (mcpIndex === -1) return;

  const subcommand = args[mcpIndex + 1] ?? 'help';
  
  const configDir = resolve(homedir(), '.vexalyn');
  const mcpConfigFile = resolve(configDir, 'mcp.json');

  async function loadMcpConfig(): Promise<Record<string, { command: string; args: string[]; env?: Record<string, string> }>> {
    if (!existsSync(mcpConfigFile)) return {};
    try {
      return JSON.parse(readFileSync(mcpConfigFile, 'utf8'));
    } catch {
      return {};
    }
  }

  async function saveMcpConfig(config: Record<string, { command: string; args: string[]; env?: Record<string, string> }>): Promise<void> {
    if (!existsSync(configDir)) {
      mkdirSync(configDir, { recursive: true });
    }
    writeFileSync(mcpConfigFile, JSON.stringify(config, null, 2), 'utf8');
  }

  if (subcommand === 'add') {
    const name = args[mcpIndex + 2] ?? '';
    const command = args[mcpIndex + 3] ?? '';
    const cmdArgs = args.slice(mcpIndex + 4).join(' ');
    
    if (!name || !command) {
      console.error('Usage: vexalyn mcp add <name> <command> [args...]');
      console.error('Example: vexalyn mcp add myserver npx -y @modelcontextprotocol/server-filesystem /path/to/dir');
      process.exit(1);
    }

    const config = await loadMcpConfig();
    config[name] = { 
      command, 
      args: cmdArgs ? cmdArgs.split(' ') : [],
      env: {}
    };
    await saveMcpConfig(config);
    console.log(`\n✅ MCP server "${name}" ditambahkan`);
    console.log(`   Command: ${command} ${cmdArgs}`);
    console.log(`   Config saved to ${mcpConfigFile}`);
  } else if (subcommand === 'list' || subcommand === 'ls') {
    const config = await loadMcpConfig();
    const servers = Object.keys(config);
    if (servers.length === 0) {
      console.log('Tidak ada MCP server terkonfigurasi.');
    } else {
      console.log('\nMCP Servers:');
      for (const name of servers) {
        const server = config[name]!;
        console.log(`  ✓ ${name}`);
        console.log(`    Command: ${server.command} ${server.args.join(' ')}`);
      }
    }
  } else if (subcommand === 'remove' || subcommand === 'rm') {
    const name = args[mcpIndex + 2] ?? '';
    if (!name) {
      console.error('Usage: vexalyn mcp remove <name>');
      process.exit(1);
    }
    
    const config = await loadMcpConfig();
    if (config[name]) {
      delete config[name];
      await saveMcpConfig(config);
      console.log(`✅ MCP server "${name}" dihapus`);
    } else {
      console.log(`MCP server "${name}" tidak ditemukan`);
    }
  } else {
    console.log(`Usage: vexalyn mcp <add|list|remove> [name] [command] [args...]`);
    console.log('  add <name> <command> [args...]  - Tambah MCP server');
    console.log('  list                            - Tampilkan MCP servers');
    console.log('  remove <name>                   - Hapus MCP server');
  }
}

async function runUpgradeCommand(): Promise<void> {
  const args = process.argv.slice(2);
  const upgradeIndex = args.findIndex(a => a === 'upgrade');
  if (upgradeIndex === -1) return;

  const targetVersion = args[upgradeIndex + 1] ?? 'latest';
  
  console.log(`\n🔄 Checking for updates...`);
  
  try {
    // Try to get latest version from npm
    const { execSync } = await import('node:child_process');
    const pkgName = 'vexalyn-code';
    const versionSpec = targetVersion === 'latest' ? pkgName : `${pkgName}@${targetVersion}`;
    
    console.log(`📦 Installing ${versionSpec}...`);
    
    // Use pnpm to install globally
    execSync(`pnpm add -g ${versionSpec}`, { 
      encoding: 'utf8',
      stdio: 'inherit'
    });
    
    console.log(`\n✅ Updated to ${versionSpec}`);
    console.log('Restart Vexalyn to use the new version.');
  } catch (error) {
    console.error('\n❌ Update failed:', error instanceof Error ? error.message : String(error));
    console.log('\nManual update: pnpm add -g vexalyn-code@latest');
    process.exit(1);
  }
}

async function runDoctorCommand(): Promise<void> {
  console.log('\n🏥 VEXALYN CODE Health Check\n');
  
  let allOk = true;
  
  // Check Node.js version
  const nodeVersion = process.version;
  const versionMatch = nodeVersion.slice(1).match(/^(\d+)/);
  const majorVersion = versionMatch && versionMatch[1] ? parseInt(versionMatch[1], 10) : 0;
  if (majorVersion >= 18) {
    console.log(`✅ Node.js ${nodeVersion} (>= 18)`);
  } else {
    console.log(`❌ Node.js ${nodeVersion} (requires >= 18)`);
    allOk = false;
  }
  
  // Check pnpm
  try {
    const { execSync } = await import('node:child_process');
    const pnpmVersion = execSync('pnpm --version', { encoding: 'utf8', stdio: 'pipe' }).trim();
    console.log(`✅ pnpm ${pnpmVersion}`);
  } catch {
    console.log('❌ pnpm not found');
    allOk = false;
  }
  
  // Check git
  try {
    const { execSync } = await import('node:child_process');
    const gitVersion = execSync('git --version', { encoding: 'utf8', stdio: 'pipe' }).trim();
    console.log(`✅ ${gitVersion}`);
  } catch {
    console.log('❌ git not found');
    allOk = false;
  }
  
  // Check workspace
  console.log(`\n📁 Workspace: ${workspaceRoot}`);
  if (existsSync(resolve(workspaceRoot, '.git'))) {
    console.log('✅ Git repository detected');
  } else {
    console.log('⚠️  Not a git repository (some features may be limited)');
  }
  
  // Check config files
  const vexalynDir = resolve(homedir(), '.vexalyn');
  if (existsSync(vexalynDir)) {
    console.log('✅ ~/.vexalyn directory exists');
    
    const authFile = resolve(vexalynDir, 'auth.json');
    if (existsSync(authFile)) {
      try {
        const auth = JSON.parse(readFileSync(authFile, 'utf8'));
        const providerCount = Object.keys(auth).length;
        console.log(`✅ Auth config: ${providerCount} provider(s) configured`);
      } catch {
        console.log('⚠️  Auth config exists but invalid JSON');
      }
    } else {
      console.log('ℹ️  No auth config found (run: vexalyn auth login)');
    }
    
    const mcpFile = resolve(vexalynDir, 'mcp.json');
    if (existsSync(mcpFile)) {
      try {
        const mcp = JSON.parse(readFileSync(mcpFile, 'utf8'));
        const serverCount = Object.keys(mcp).length;
        console.log(`✅ MCP config: ${serverCount} server(s) configured`);
      } catch {
        console.log('⚠️  MCP config exists but invalid JSON');
      }
    }
  } else {
    console.log('ℹ️  ~/.vexalyn directory not found (will be created on first run)');
  }
  
  // Check provider availability
  console.log('\n🤖 Provider Status:');
  const providers = ['qwen', 'openai', 'gemini', 'anthropic', 'deepseek', 'groq', 'openrouter'];
  for (const provider of providers) {
    const envKey = `VEXALYN_${provider.toUpperCase()}_API_KEY`;
    const hasKey = !!process.env[envKey];
    const status = hasKey ? '✅' : '⭕';
    const label = hasKey ? 'configured' : 'not configured';
    console.log(`  ${status} ${provider}: ${label}`);
  }
  
  console.log('\n' + (allOk ? '✅ All checks passed!' : '⚠️  Some issues found. Run with --help for more info.'));
}

async function runModelsCommand(): Promise<void> {
  const { DEFAULT_PROVIDER_MODELS, PROVIDER_LABELS } = await import('./commands.js');
  console.log('\n' + '\x1b[38;2;168;85;247m◆ Available Models\x1b[0m\n');
  for (const [prov, models] of Object.entries(DEFAULT_PROVIDER_MODELS)) {
    const label = (PROVIDER_LABELS as Record<string,string>)[prov] ?? prov;
    console.log(`  \x1b[1m${label} (${prov})\x1b[0m`);
    for (const m of models.slice(0, 5)) console.log(`    • ${m}`);
    console.log('');
  }
  console.log('Gunakan: vexalyn --model <provider/model> atau /model di TUI\n');
}

async function runAgentCommand(): Promise<void> {
  const args = process.argv.slice(2);
  const sub = args[1] ?? 'help';
  const agentsDir = resolve(homedir(), '.vexalyn', 'agents');
  if (sub === 'list' || sub === 'ls') {
    if (!existsSync(agentsDir)) { console.log('Tidak ada custom agent.'); return; }
    const files = readdirSync(agentsDir).filter(f => f.endsWith('.json'));
    if (files.length === 0) console.log('Tidak ada custom agent.');
    else { console.log('\nAgents:'); for (const f of files) console.log('  • ' + f.replace('.json','')); }
    return;
  }
  if (sub === 'create') {
    const name = args[2];
    if (!name) { console.error('Usage: vexalyn agent create <name>'); process.exit(1); }
    if (!existsSync(agentsDir)) mkdirSync(agentsDir, { recursive: true });
    const file = resolve(agentsDir, name + '.json');
    if (existsSync(file)) { console.error('Agent sudah ada: ' + name); process.exit(1); }
    writeFileSync(file, JSON.stringify({ name, systemPrompt: 'Custom agent ' + name, permissions: [], model: 'gpt-4o-mini' }, null, 2));
    console.log('✅ Agent "' + name + '" dibuat di ' + file);
    return;
  }
  console.log('Usage: vexalyn agent <create <name>|list>');
}

async function runAttachCommand(): Promise<void> {
  const target = process.argv.slice(2)[1] ?? '';
  console.log('\n🔗 Attach' + (target ? ' ke ' + target : ''));
  console.log('Fitur attach remote session — stub. Gunakan vexalyn serve di host lalu vexalyn attach <url>.');
}

async function runGithubCommand(): Promise<void> {
  const sub = process.argv.slice(2)[1] ?? 'help';
  if (sub === 'install') { console.log('✅ GitHub integration terinstall (stub). Tambahkan GITHUB_TOKEN di .env'); return; }
  if (sub === 'run') { console.log('🚀 Menjalankan GitHub agent (stub)...'); return; }
  console.log('Usage: vexalyn github <install|run>');
}

async function runMcpAuthCommand(): Promise<void> {
  const name = process.argv.slice(2)[2] ?? '';
  if (!name) { console.error('Usage: vexalyn mcp auth <name>'); process.exit(1); }
  console.log('🔐 MCP auth untuk ' + name + ' — simpan token di ~/.vexalyn/mcp.json env');
}
async function runMcpLogoutCommand(): Promise<void> {
  const name = process.argv.slice(2)[2] ?? '';
  if (!name) { console.error('Usage: vexalyn mcp logout <name>'); process.exit(1); }
  console.log('✅ MCP logout ' + name);
}
async function runMcpDebugCommand(): Promise<void> {
  const name = process.argv.slice(2)[2] ?? '';
  console.log('🐛 MCP debug' + (name ? ' ' + name : '') + ' — cek koneksi MCP server di ~/.vexalyn/mcp.json');
  const cfg = resolve(homedir(), '.vexalyn', 'mcp.json');
  if (existsSync(cfg)) console.log(readFileSync(cfg, 'utf8')); else console.log('(tidak ada mcp.json)');
}

async function runServeCommand(portFlag: string, hostFlag: string): Promise<void> {
  const port = portFlag || process.env.PORT || '3000';
  const host = hostFlag || '0.0.0.0';
  console.log('\n🚀 Vexalyn headless server listening di http://' + host + ':' + port);
  console.log('(stub) Implementasikan HTTP server headless di sini. Tekan Ctrl+C untuk stop.');
  // keep alive
  await new Promise(() => {});
}
async function runWebCommand(portFlag: string): Promise<void> {
  const port = portFlag || process.argv.slice(2)[1] || '3000';
  console.log('\n🌐 Web interface di http://localhost:' + port + ' (stub) — jalankan vexalyn serve untuk API.');
}
async function runAcpCommand(): Promise<void> {
  console.log('ACP server (stub) — Agent Client Protocol. Menunggu koneksi stdin/stdout...');
}

async function runSessionCommand(): Promise<void> {
  const sub = process.argv.slice(2)[1] ?? 'list';
  const sessionsDir = resolve(process.cwd(), '.vexalyn', 'sessions');
  if (sub === 'list' || sub === 'ls') {
    if (!existsSync(sessionsDir)) { console.log('Tidak ada session.'); return; }
    const files = readdirSync(sessionsDir).filter(f => f.endsWith('.json')).sort();
    if (files.length === 0) console.log('Tidak ada session.');
    else { console.log('\nSessions:'); for (const f of files) console.log('  • ' + f); }
    return;
  }
  if (sub === 'delete' || sub === 'rm') {
    const id = process.argv.slice(2)[2];
    if (!id) { console.error('Usage: vexalyn session delete <id>'); process.exit(1); }
    const target = resolve(sessionsDir, id.endsWith('.json') ? id : id + '.json');
    if (!existsSync(target)) { console.error('Session tidak ditemukan: ' + id); process.exit(1); }
    unlinkSync(target);
    console.log('✅ Session dihapus: ' + id);
    return;
  }
  console.log('Usage: vexalyn session <list|delete <id>>');
}

async function runExportCommand(): Promise<void> {
  const outFile = process.argv.slice(2)[1] ?? resolve(process.cwd(), 'vexalyn-export.md');
  const histFile = resolve(process.cwd(), '.turbo/vexalyn-history.json');
  if (!existsSync(histFile)) { console.error('Tidak ada history untuk di-export.'); process.exit(1); }
  const msgs = JSON.parse(readFileSync(histFile, 'utf8'));
  const md = Array.isArray(msgs) ? msgs.map((m: {role:string;content:string}) => '## ' + m.role + '\n' + m.content).join('\n\n---\n\n') : '';
  writeFileSync(outFile, '# Vexalyn Export\n\n' + md, 'utf8');
  console.log('✅ Export ke ' + outFile);
}
async function runImportCommand(): Promise<void> {
  const inFile = process.argv.slice(2)[1];
  if (!inFile || !existsSync(inFile)) { console.error('Usage: vexalyn import <file>'); process.exit(1); }
  const content = readFileSync(inFile, 'utf8');
  // naive: cari blok role
  const histFile = resolve(process.cwd(), '.turbo/vexalyn-history.json');
  writeFileSync(histFile, JSON.stringify([{ role: 'user', content }], null, 2));
  console.log('✅ Import dari ' + inFile + ' ke ' + histFile);
}

async function runPluginCommand(): Promise<void> {
  const sub = process.argv.slice(2)[1] ?? 'list';
  console.log('🔌 Plugin ' + sub + ' (stub) — kelola plugin di ~/.vexalyn/plugins/');
}
async function runPrCommand(): Promise<void> {
  const pr = process.argv.slice(2)[1] ?? '';
  console.log('🔀 PR handler' + (pr ? ' #' + pr : '') + ' (stub) — checkout PR via gh pr checkout');
}
async function runDbCommand(): Promise<void> {
  const sub = process.argv.slice(2)[1] ?? 'path';
  const dbPath = resolve(homedir(), '.vexalyn', 'vexalyn.db');
  if (sub === 'path') { console.log(dbPath); return; }
  if (sub === 'query') {
    const sql = process.argv.slice(3).join(' ');
    if (!sql) { console.error('Usage: vexalyn db query <sql>'); process.exit(1); }
    console.log('(stub) Query: ' + sql + ' di ' + dbPath);
    return;
  }
  console.log('Usage: vexalyn db <query <sql>|path>');
}
async function runDebugCommand(): Promise<void> {
  console.log('\n🐛 Debug Info');
  console.log('  version: ' + cliVersion);
  console.log('  node: ' + process.version);
  console.log('  workspace: ' + process.cwd());
  console.log('  config: ' + JSON.stringify((() => { try { return JSON.parse(readFileSync(resolve(homedir(), '.vexalyn', 'mcp.json'), 'utf8')); } catch { return {}; } })(), null, 2));
}
async function runUninstallCommand(): Promise<void> {
  console.log('\n🗑️  Uninstall Vexalyn — jalankan: pnpm remove -g vexalyn-code  atau  npm uninstall -g vexalyn-code');
}

const workspaceRoot = process.cwd();
const pkgPath = resolve(fileURLToPath(new URL('../package.json', import.meta.url)));
let cliVersion = '1.9.1';

try {
  if (existsSync(pkgPath)) {
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf8'));
    if (typeof pkg.version === 'string' && pkg.version.trim()) cliVersion = pkg.version;
  }
} catch {}

function printCliHelp(): void {
  console.log(`
 VEXALYN CODE v${cliVersion} — AI Coding Agent

 USAGE
   vexalyn [command] [options]          TUI interactive (default)
   vexalyn run <prompt> [options]       Non-interactive

 CLI COMMANDS
   vexalyn                              TUI start (interactive)
   vexalyn run <prompt>                 Non-interactive mode
   vexalyn agent create <name>          Buat custom agent
   vexalyn agent list                   Daftar agent
   vexalyn attach [session]             Attach ke remote session
   vexalyn auth login <provider>        Login provider AI
   vexalyn auth list                    Daftar provider terhubung
   vexalyn auth logout <provider>       Logout provider
   vexalyn github install               Install GitHub integration
   vexalyn github run                   Jalankan GitHub agent
   vexalyn mcp add <name> <cmd>         Tambah MCP server
   vexalyn mcp list                     Daftar MCP server
   vexalyn mcp auth <name>              Auth MCP server
   vexalyn mcp logout <name>            Logout MCP
   vexalyn mcp debug <name>             Debug MCP
   vexalyn models                       Daftar model AI
   vexalyn serve [options]              Headless HTTP server
   vexalyn session list                 Daftar semua session
   vexalyn session delete <id>          Hapus session
   vexalyn export [file]                Export session ke Markdown
   vexalyn import <file>                Import session
   vexalyn web [port]                   Web interface
   vexalyn acp                          ACP server
   vexalyn plugin [list|add|remove]     Plugin management
   vexalyn pr [number]                  Handle GitHub PR
   vexalyn db query <sql>               Query database
   vexalyn db path                      Lokasi database
   vexalyn debug                        Debugging info
   vexalyn uninstall                    Uninstall Vexalyn
   vexalyn upgrade [version]            Upgrade Vexalyn
   vexalyn stats                        Token usage stats
   vexalyn doctor                       Health check

 GLOBAL FLAGS
   --help, -h           Help
   --version, -v        Version
   --print-logs         Print logs langsung
   --log-level <level>  debug|info|warn|error
   --pure               Mode minimal (tanpa VEXALYN.md/SKILL.md)

 TUI START FLAGS
   --continue, -c       Lanjutkan session terakhir
   --session, -s <id>   Buka session tertentu
   --fork <id>          Fork session
   --prompt <text>      Prompt awal langsung
   --model, -m <model>  Model AI (provider/model)
   --agent <name>       Agent yang dipakai
   --auto               Mode otomatis (skip permission prompt)
   --port <port>        Port server
   --hostname <host>    Hostname server
`);
}

const argv = process.argv.slice(2);
if (argv.includes('--help') || argv.includes('-h')) { printCliHelp(); process.exit(0); }
if (argv.includes('--version') || argv.includes('-v')) { console.log(cliVersion); process.exit(0); }

// Global flags handling (set env before loadEnvFiles)
if (argv.includes('--print-logs')) process.env.VEXALYN_PRINT_LOGS = 'true';
const logLevelIdx = argv.indexOf('--log-level');
if (logLevelIdx !== -1 && argv[logLevelIdx + 1]) process.env.VEXALYN_LOG_LEVEL = argv[logLevelIdx + 1]!;
if (argv.includes('--pure')) process.env.VEXALYN_PURE = 'true';

// TUI start flags - parse before TUI bootstrap
const tuiContinueFlag = argv.includes('--continue') || argv.includes('-c');
let tuiSessionFlag = '';
let tuiForkFlag = '';
let tuiPromptFlag = '';
let tuiModelFlag = '';
let tuiAgentFlag = '';
const tuiAutoFlag = argv.includes('--auto');
let tuiPortFlag = '';
let tuiHostnameFlag = '';
{
  const sIdx = argv.findIndex(a => a === '--session' || a === '-s');
  if (sIdx !== -1 && argv[sIdx + 1]) tuiSessionFlag = argv[sIdx + 1]!;
  const fIdx = argv.indexOf('--fork');
  if (fIdx !== -1 && argv[fIdx + 1]) tuiForkFlag = argv[fIdx + 1]!;
  const pIdx = argv.indexOf('--prompt');
  if (pIdx !== -1 && argv[pIdx + 1]) tuiPromptFlag = argv.slice(pIdx + 1).join(' ');
  const mIdx = argv.findIndex(a => a === '--model' || a === '-m');
  if (mIdx !== -1 && argv[mIdx + 1]) tuiModelFlag = argv[mIdx + 1]!;
  const aIdx = argv.indexOf('--agent');
  if (aIdx !== -1 && argv[aIdx + 1]) tuiAgentFlag = argv[aIdx + 1]!;
  const portIdx = argv.indexOf('--port');
  if (portIdx !== -1 && argv[portIdx + 1]) tuiPortFlag = argv[portIdx + 1]!;
  const hostIdx = argv.indexOf('--hostname');
  if (hostIdx !== -1 && argv[hostIdx + 1]) tuiHostnameFlag = argv[hostIdx + 1]!;
  if (tuiModelFlag) process.env.VEXALYN_DEFAULT_MODEL = tuiModelFlag.split('/').pop() ?? tuiModelFlag;
  if (tuiModelFlag && tuiModelFlag.includes('/')) {
    const prov = tuiModelFlag.split('/')[0]!;
    if (prov) process.env.VEXALYN_DEFAULT_PROVIDER = prov;
  }
}

// ── vexalyn run — non-interactive mode ──────────────────────────────────────────
if (argv[0] === 'run' || argv.includes('run')) {
  await runNonInteractiveMode();
  process.exit(0);
}
// ── vexalyn models ────────────────────────────────────────────────────────────
if (argv[0] === 'models') { await runModelsCommand(); process.exit(0); }
// ── vexalyn agent ───────────────────────────────────────────────────────────
if (argv[0] === 'agent') { await runAgentCommand(); process.exit(0); }
// ── vexalyn attach ──────────────────────────────────────────────────────────
if (argv[0] === 'attach') { await runAttachCommand(); process.exit(0); }
// ── vexalyn auth — provider auth management ─────────────────────────────────────
if (argv[0] === 'auth') { await runAuthCommand(); process.exit(0); }
// ── vexalyn github ──────────────────────────────────────────────────────────
if (argv[0] === 'github') { await runGithubCommand(); process.exit(0); }
// ── vexalyn mcp — MCP server management ─────────────────────────────────────────
if (argv[0] === 'mcp') {
  const sub = argv[1];
  if (sub === 'auth') { await runMcpAuthCommand(); process.exit(0); }
  if (sub === 'logout') { await runMcpLogoutCommand(); process.exit(0); }
  if (sub === 'debug') { await runMcpDebugCommand(); process.exit(0); }
  await runMcpCommand(); process.exit(0);
}
// ── vexalyn serve / web / acp ───────────────────────────────────────────────
if (argv[0] === 'serve') { await runServeCommand(tuiPortFlag, tuiHostnameFlag); process.exit(0); }
if (argv[0] === 'web') { await runWebCommand(tuiPortFlag); process.exit(0); }
if (argv[0] === 'acp') { await runAcpCommand(); process.exit(0); }
// ── vexalyn session ─────────────────────────────────────────────────────────
if (argv[0] === 'session') { await runSessionCommand(); process.exit(0); }
// ── vexalyn export / import ─────────────────────────────────────────────────
if (argv[0] === 'export') { await runExportCommand(); process.exit(0); }
if (argv[0] === 'import') { await runImportCommand(); process.exit(0); }
// ── vexalyn plugin / pr / db / debug / uninstall ────────────────────────────
if (argv[0] === 'plugin') { await runPluginCommand(); process.exit(0); }
if (argv[0] === 'pr') { await runPrCommand(); process.exit(0); }
if (argv[0] === 'db') { await runDbCommand(); process.exit(0); }
if (argv[0] === 'debug') { await runDebugCommand(); process.exit(0); }
if (argv[0] === 'uninstall') { await runUninstallCommand(); process.exit(0); }
// ── vexalyn upgrade — CLI self-update ────────────────────────────────────────────
if (argv[0] === 'upgrade') { await runUpgradeCommand(); process.exit(0); }
if (argv[0] === 'stats') {
  console.log('Gunakan /stats di dalam TUI untuk melihat token usage.');
  process.exit(0);
}
// ── vexalyn doctor — diagnostics ────────────────────────────────────────────────
if (argv[0] === 'doctor') { await runDoctorCommand(); process.exit(0); }

loadEnvFiles(workspaceRoot, import.meta.url);

// ── Smart defaults — only applied if env vars are not already set ──────────────
// Instead of hardcoding one provider, pick the first provider whose credentials
// are actually available so the CLI starts cleanly without a .env file.
const PROVIDER_FALLBACK_ORDER: ProviderName[] = [
  'qwen', 'openai', 'gemini', 'anthropic', 'deepseek', 'groq', 'openrouter',
  'zai', 'moonshot', 'mistral', 'xai', 'cerebras', 'together', 'fireworks',
  'deepinfra', 'siliconflow', 'nvidia', 'cohere', 'perplexity', 'sambanova',
  'novita', 'ai21', 'ollama',
];
if (!process.env.VEXALYN_DEFAULT_PROVIDER) {
  process.env.VEXALYN_DEFAULT_PROVIDER = PROVIDER_FALLBACK_ORDER.find(hasConfiguredProvider) ?? 'qwen';
}
if (!process.env.VEXALYN_DEFAULT_MODEL) {
  const fallbackModels = DEFAULT_PROVIDER_MODELS[process.env.VEXALYN_DEFAULT_PROVIDER as ProviderName];
  process.env.VEXALYN_DEFAULT_MODEL = fallbackModels?.[0] ?? 'gpt-4o-mini';
}

const config = loadConfig(process.env, workspaceRoot);
const logger = createLogger(config.logLevel);
let activeProvider = initActiveProvider(config);
let activeProviderName = config.defaultProvider;
let activeModelName    = config.defaultModel;

// Proxy that always delegates to the current activeProvider — allows live model swap without restart
const providerProxy = new Proxy({} as typeof activeProvider, {
  get(_target, prop) {
    const val = (activeProvider as unknown as Record<string, unknown>)[prop as string];
    if (typeof val === 'function') return val.bind(activeProvider);
    return val;
  },
  has(_target, prop) {
    return prop in activeProvider;
  },
});
const permissionManager = new PermissionManager(true, config.permissions ?? []);
const planManager = new PlanManager();
const toolRegistry = createFilesystemToolRegistry(planManager)
  .register(createTasksTool)
  .register(completeTaskTool);
const availableSkills = await listSkills(workspaceRoot).catch(() => [] as string[]);
let muteEcho = false;
const originalWrite = process.stdout.write.bind(process.stdout);
process.stdout.write = function (
  this: typeof process.stdout,
  chunk: string | Uint8Array,
  ...args: unknown[]
): boolean {
  if (muteEcho) return true;
  return (originalWrite as unknown as (c: typeof chunk, ...a: typeof args) => boolean)(chunk, ...args);
} as unknown as typeof process.stdout.write;

let pasteBuffer = '';
let inBracketedPaste = false;
const pasteInput = new Transform({
  transform(chunk: Buffer, _encoding, callback) {
    let data = chunk.toString();
    const startMarker = '\x1b[200~';
    const endMarker = '\x1b[201~';

    while (data.length > 0) {
      if (!inBracketedPaste) {
        const start = data.indexOf(startMarker);
        if (start === -1) {
          this.push(data);
          break;
        }
        if (start > 0) this.push(data.slice(0, start));
        inBracketedPaste = true;
        data = data.slice(start + startMarker.length);
        continue;
      }

      const end = data.indexOf(endMarker);
      if (end === -1) {
        pasteBuffer += data;
        break;
      }
      pasteBuffer += data.slice(0, end);
      const normalized = pasteBuffer.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
      if (normalized) {
        muteEcho = true;
        this.push(normalized + '\n');
        muteEcho = false;
      }
      pasteBuffer = '';
      inBracketedPaste = false;
      data = data.slice(end + endMarker.length);
    }
    callback();
  },
  flush(callback) {
    if (pasteBuffer.trim()) {
      const normalized = pasteBuffer.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
      muteEcho = true;
      this.push(normalized + '\n');
      muteEcho = false;
    }
    callback();
  },
});
process.stdin.pipe(pasteInput);
process.stdout.write('\x1b[?2004h');
const readline = createInterface({ input: pasteInput, output: process.stdout, prompt: '> ' });
const infoBadge = '\u001b[44m\u001b[97m INFO \u001b[0m';
let permissionResolve: ((allowed: boolean) => void) | null = null;
let readlineClosed = false;
let askResolve: ((result: { answers: string[] }) => void) | null = null;
let activeQuestion: { question: string; options?: string[]; multiple?: boolean } | null = null;

const purple = '\x1b[38;2;168;85;247m';
const reset = '\x1b[0m';
const bold = '\x1b[1m';
const cyan = '\x1b[96m';
const grey = '\x1b[90m';

const historyFilePath = resolve(workspaceRoot, '.turbo/vexalyn-history.json');
const userProfilePath = resolve(workspaceRoot, '.turbo/user/profile.json');
const userSettingsPath = resolve(workspaceRoot, '.turbo/user/settings.json');
const maxContextTokens = Math.max(4096, Number.parseInt(process.env.VEXALYN_CONTEXT_TOKEN_LIMIT || '128000', 10) || 128000);

interface UserSettings {
  planModeEnabled: boolean;
}

// ── User profile helpers ──────────────────────────────────────────────────────
function loadUserProfile(): { name: string } | null {
  if (!existsSync(userProfilePath)) return null;
  try {
    const data = JSON.parse(readFileSync(userProfilePath, 'utf8'));
    if (data && typeof data.name === 'string' && data.name.trim()) return { name: data.name.trim() };
  } catch {}
  return null;
}

function saveUserProfile(name: string): void {
  try {
    const dir = resolve(workspaceRoot, '.turbo/user');
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
    writeFileSync(userProfilePath, JSON.stringify({ name }, null, 2), 'utf8');
  } catch {}
}

function deleteUserProfile(): void {
  try {
    if (existsSync(userProfilePath)) unlinkSync(userProfilePath);
  } catch {}
}

function loadUserSettings(): UserSettings {
  if (!existsSync(userSettingsPath)) return { planModeEnabled: true };
  try {
    const data = JSON.parse(readFileSync(userSettingsPath, 'utf8'));
    return { planModeEnabled: data?.planModeEnabled !== false };
  } catch {
    return { planModeEnabled: true };
  }
}

function saveUserSettings(settings: UserSettings): void {
  try {
    const dir = resolve(workspaceRoot, '.turbo/user');
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
    writeFileSync(userSettingsPath, JSON.stringify(settings, null, 2), 'utf8');
  } catch {}
}

function getRestoredCount(): number {
  if (!existsSync(historyFilePath)) return 0;
  try {
    const data = readFileSync(historyFilePath, 'utf8');
    const messages = JSON.parse(data);
    if (Array.isArray(messages)) {
      return messages.filter((msg) => msg && typeof msg === 'object' && (msg.role === 'user' || msg.role === 'assistant')).length;
    }
  } catch {}
  return 0;
}

function readRootKnowledgeFile(primaryName: string, aliases: string[], truncateLabel: string, limit = 18000): string {
  const names = [primaryName, ...aliases];
  const candidates = names.flatMap((name) => [
    resolve(workspaceRoot, name),
    resolve(fileURLToPath(new URL(`../../../${name}`, import.meta.url))),
  ]);
  const seen = new Set<string>();

  for (const candidate of candidates) {
    const key = candidate.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    try {
      if (!existsSync(candidate)) continue;
      const content = readFileSync(candidate, 'utf8').trim();
      if (!content) continue;
      return content.length > limit
        ? `${content.slice(0, limit)}\n\n...[${truncateLabel} truncated for context budget]`
        : content;
    } catch {}
  }

  return '';
}

function buildGlobalProtocolContext(): string {
  if (process.env.VEXALYN_PURE === 'true') return '(Pure mode — VEXALYN.md/SKILL.md tidak dimuat)';
  const vexalynBrain = readRootKnowledgeFile('VEXALYN.md', ['vexalyn.md'], 'VEXALYN.md');
  const executionProtocol = readRootKnowledgeFile('SKILL.md', ['skill.md'], 'SKILL.md');

  return [
    'VEXALYN.md CORE BRAIN:',
    vexalynBrain || '(Tidak ada VEXALYN.md yang ditemukan di workspace/root paket.)',
    '',
    'SKILL.md GLOBAL EXECUTION PROTOCOL:',
    executionProtocol || '(Tidak ada SKILL.md yang ditemukan di workspace/root paket.)',
  ].join('\n');
}

function estimateTokens(text: string): number {
  if (!text) return 0;
  return Math.ceil(text.replace(/\s+/g, ' ').trim().length / 4);
}

function getUsageInfo(systemPromptText: string) {
  const historyTokens = agent.history.list().reduce((total, message) => total + estimateTokens(message.content), 0);
  const usedTokens = Math.max(historyTokens, estimateTokens(systemPromptText));
  return {
    usedTokens,
    remainingTokens: Math.max(0, maxContextTokens - usedTokens),
    maxTokens: maxContextTokens,
  };
}

function cardLine(content: string, width = 58): string {
  const strippedLen = content.replace(/\x1b\[[0-9;]*m/g, '').length;
  const pad = Math.max(0, width - strippedLen);
  return `${purple}│${reset} ${content}${' '.repeat(pad)} ${purple}│${reset}`;
}

function boxBorder(kind: 'top' | 'middle' | 'bottom', width = cWidth): string {
  const chars = kind === 'top' ? ['╭', '╮'] : kind === 'middle' ? ['├', '┤'] : ['╰', '╯'];
  return `${purple}${chars[0]}${'─'.repeat(width + 2)}${chars[1]}${reset}`;
}

function promptText(): string {
  return `${purple}◆${reset} ${bold}${username}${reset} ${purple}›${reset} `;
}

function plainPromptText(): string {
  return `◆ ${username} › `;
}

function truncateMiddle(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  const keep = Math.max(4, Math.floor((maxLength - 3) / 2));
  return `${value.slice(0, keep)}...${value.slice(-keep)}`;
}

async function writeQuestionPrompt(request: { question: string; options?: string[]; multiple?: boolean }): Promise<void> {
  const width = Math.min(74, Math.max(cWidth, 40));
  const lines = [
    '',
    boxBorder('top', width),
    cardLine(`${bold}${purple}\u25c6 Question${reset}`, width),
    boxBorder('middle', width),
  ];
  for (const qLine of request.question.split('\n')) {
    lines.push(cardLine(`  ${cyan}${qLine}${reset}`, width));
  }
  if (request.options && request.options.length > 0) {
    lines.push(boxBorder('middle', width));
    request.options.forEach((option, index) => {
      lines.push(cardLine(`  ${grey}${index + 1}.${reset} ${option}`, width));
    });
    const hint = request.multiple ? 'pilih angka (pisah koma) atau ketik jawaban sendiri' : 'pilih angka atau ketik jawaban sendiri';
    lines.push(cardLine(`  ${grey}(${hint})${reset}`, width));
  }
  lines.push(boxBorder('bottom', width));

  for (const line of lines) {
    process.stdout.write(line + '\n');
    await sleep(8);
  }
  process.stdout.write(`${purple}?${reset} Jawaban: `);
}

async function writePermissionPrompt(action: string, path: string): Promise<void> {
  const width = Math.min(74, Math.max(cWidth, 34, Math.min(path.length + 12, 74)));
  const target = truncateMiddle(path, Math.max(12, width - 12));
  const lines = [
    '',
    boxBorder('top', width),
    cardLine(`${bold}${purple}◆ Permission Required${reset}`, width),
    boxBorder('middle', width),
    cardLine(`  \x1b[90mAction\x1b[0m  \x1b[97m${action}\x1b[0m`, width),
    cardLine(`  \x1b[90mTarget\x1b[0m  \x1b[97m${target}\x1b[0m`, width),
    boxBorder('bottom', width),
  ];

  for (const line of lines) {
    process.stdout.write(line + '\n');
    await sleep(8);
  }
  process.stdout.write(`${purple}?${reset} Allow ${action}? \x1b[90m[y/N]\x1b[0m `);
}

const restoredCount = getRestoredCount();
const sessionText = restoredCount > 0 ? `Restored · ${restoredCount} messages` : 'Fresh start';
const cWidth = 44;
const settings = loadUserSettings();
let planModeEnabled = settings.planModeEnabled;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function clearSubmittedInput(line: string): void {
  const prompt = plainPromptText();
  const cols = process.stdout.columns || 80;
  const totalLength = prompt.length + line.length;
  const rows = Math.ceil(totalLength / cols);
  for (let i = 0; i < rows; i++) {
    (originalWrite as unknown as (c: string) => void)('\x1b[1A\r\x1b[2K');
  }
}

/**
 * Print plain lines with a per-line delay (used for card UI after the ASCII header).
 */
async function printTypedLines(lines: string[], delayMs = 15) {
  for (const line of lines) {
    console.log(line);
    await sleep(delayMs);
  }
}


// 1a. ASCII header — langsung muncul sekaligus
await printTypedLines([
  "",
  `${purple}██╗   ██╗███████╗██╗  ██╗ █████╗ ██╗  ██╗   ██╗███╗   ██╗     ██████╗ ██████╗ ██████╗ ███████╗${reset}`,
  `${purple}██║   ██║██╔════╝╚██╗██╔╝██╔══██╗██║  ╚██╗ ██╔╝████╗  ██║    ██╔════╝██╔═══██╗██╔══██╗██╔════╝${reset}`,
  `${purple}██║   ██║█████╗   ╚███╔╝ ███████║██║   ╚████╔╝ ██╔██╗ ██║    ██║     ██║   ██║██║  ██║█████╗  ${reset}`,
  `${purple}╚██╗ ██╔╝██╔══╝   ██╔██╗ ██╔══██║██║    ╚██╔╝  ██║╚██╗██║    ██║     ██║   ██║██║  ██║██╔══╝  ${reset}`,
  `${purple} ╚████╔╝ ███████╗██╔╝ ██╗██║  ██║███████╗██║   ██║ ╚████║    ╚██████╗╚██████╔╝██████╔╝███████╗${reset}`,
  `${purple}  ╚═══╝  ╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═╝   ╚═╝  ╚═══╝     ╚═════╝ ╚═════╝ ╚═════╝ ╚══════╝${reset}`,
  "",
], 0);

// 1b. Card UI — instant per-line reveal
await printTypedLines([
  `${purple}╭──────────────────────────────────────────────╮${reset}`,
  `${purple}│${reset} ${bold}\x1b[97m✶ Welcome to Vexalyn Code research preview!${reset}  ${purple}│${reset}`,
  `${purple}╰──────────────────────────────────────────────╯${reset}`,
  "",
  `${purple}╭──────────────────────────────────────────────╮${reset}`,
  cardLine(`\x1b[1;95m◆ VEXALYN CODE\x1b[0m`, cWidth),
  cardLine(`  \x1b[90mAI Coding Agent\x1b[0m`, cWidth),
  cardLine(``, cWidth),
  cardLine(`\x1b[1;92m● ONLINE\x1b[0m   \x1b[36mv${cliVersion}\x1b[0m`, cWidth),
  `${purple}├──────────────────────────────────────────────┤${reset}`,
  cardLine(`\x1b[90mWorkspace\x1b[0m   \x1b[97m${workspaceRoot}\x1b[0m`, cWidth),
  cardLine(`\x1b[90mSession\x1b[0m     \x1b[97m${sessionText}\x1b[0m`, cWidth),
  cardLine(``, cWidth),
  cardLine(`\x1b[33m/help\x1b[0m  cmds  \x1b[33m/clear\x1b[0m  reset  \x1b[33m/exit\x1b[0m  quit`, cWidth),
  `${purple}╰──────────────────────────────────────────────╯${reset}`,
  ""
], 15);

// 2. Repository Map - background ala opencode (tanpa spinner blok, toast di status bar)
let repoMapSummary = '(Repository map loading...)';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future context injection when map finishes
let repoMap: Awaited<ReturnType<typeof generateRepositoryMap>> | null = null;
// Silent background indexing - status di footer, tidak dorong prompt ke bawah (persis opencode)
const mapPromise = generateRepositoryMap(workspaceRoot).then((m) => {
  repoMap = m;
  repoMapSummary = formatCompactRepositoryMap(m);
  // toast ala opencode: baris status bawah yang auto-hilang 2 detik, tidak numpuk di scrollback
  const count = Array.isArray(m) ? m.length : 0;
  process.stdout.write(`\x1b[s\x1b[?25l\r\x1b[2K\x1b[90m◍ indexed ${count} files • ${workspaceRoot}\x1b[0m\x1b[u\x1b[?25h`);
  setTimeout(() => process.stdout.write('\x1b[s\r\x1b[2K\x1b[u'), 2500);
  return m;
}).catch((err) => {
  repoMapSummary = '(Repository map gagal dimuat)';
  process.stdout.write(`\x1b[s\r\x1b[2K\x1b[90m◍ index gagal: ${err instanceof Error ? err.message : String(err)}\x1b[0m\x1b[u`);
  setTimeout(() => process.stdout.write('\x1b[s\r\x1b[2K\x1b[u'), 2500);
  return null;
});
const globalProtocolContext = buildGlobalProtocolContext();
// Jangan await mapPromise - biarkan jalan background, prompt langsung siap ketik

const systemPrompt = `Kamu adalah VEXALYN CODE, coding agent lokal yang berjalan langsung di komputer user.

WORKSPACE ROOT: ${workspaceRoot}
Semua path relatif WAJIB dianggap relatif terhadap WORKSPACE ROOT. Untuk membuat project baru, buat folder project di dalam WORKSPACE ROOT, bukan di folder instalasi Vexalyn.
REPOSITORY MAP tersedia sebagai konteks ringkas dan disimpan di WORKSPACE ROOT/.turbo/vexalyn-map.json.
TOOLS: get_project_info, list_directory, read_file, write_file, edit_file, apply_patch, delete_file, create_directory, delete_directory, get_project_structure, search_files, grep, glob, run_command, web_search, fetch_web_page, semantic_search, get_code_outline, git_status, git_diff, question, lsp, skill, create_plan, create_tasks, complete_task, todowrite, todoread, task.
Gunakan tool filesystem untuk workspace aktif. Jangan mengarang root project atau hanya menampilkan contoh; kerjakan perubahan yang diminta.

ATURAN:
- Panggil tool TANPA teks dulu. Teks hanya setelah tool selesai.
- Setelah write/edit/delete: konfirmasi 1 kalimat saja.
- Jika build/run gagal: analisis error, perbaiki sendiri, jalankan ulang.
- Untuk cari file: gunakan search_files (recursive). Untuk konsep: semantic_search.
- Untuk mencari ISI kode pakai regex, WAJIB grep (hasil "path:baris: teks", pakai include untuk filter tipe file). Untuk menemukan FILE berdasarkan pola nama seperti src/**/*.ts, WAJIB glob. grep/glob lebih presisi daripada search_files — utamakan keduanya saat analisis kode besar.
- Baca file dengan read_file, BUKAN run_command shell.
- edit_file: pakai allowMultiple:true jika teks target duplikat.
- Untuk mengubah BANYAK file sekaligus, utamakan apply_patch (satu panggilan atomik: Add/Update/Move/Delete File). Format: blok "*** Begin Patch" ... "*** End Patch"; baris konteks dan "-"/"+" harus persis seperti isi file asli.
- Jika permintaan user AMBIGU dan salah tebak bisa merusak (mis. hapus file, pilih teknologi/arsitektur, opsi perilaku), tanyakan dulu via tool question dengan 2-6 opsi singkat. Jangan bertanya untuk hal yang sudah jelas dari konteks.
- lsp untuk intelijen TS/JS: action=diagnostics memeriksa error tipe file (pakai SETELAH write/edit file TS untuk verifikasi), hover/symbols/references untuk tipe & pemakaian simbol. Butuh line+column 1-based untuk hover/references.
- SKILL TERPASANG: ${availableSkills.length > 0 ? availableSkills.join(', ') : '(tidak ada)'}. Jika tugas user cocok dengan salah satu skill, panggil tool skill dengan nama tersebut dan ikuti instruksinya.
- Gunakan git_status dan git_diff untuk memantau perubahan repositori tanpa memerlukan persetujuan eksekusi user.
- JANGAN pernah tulis tool call sebagai teks/markdown — selalu gunakan function call JSON.
- FLOW MEMBUAT PROJECT BARU / TUGAS KOMPLEKS:
  1. WAJIB panggil \`create_plan\` pertama kali untuk mengajukan rencana implementasi langkah-demi-langkah dan minta persetujuan user.
  2. Setelah rencana disetujui, panggil \`create_tasks\` untuk menginisialisasi checklist tugas visual. Checklist harus ringkas, maksimal 6-7 milestone berbasis file/aksi utama, bukan task kecil per section.
  3. Setiap kali menyelesaikan pekerjaan untuk satu task/langkah, panggil \`complete_task\` dengan ID tugas yang bersangkutan agar tercentang hijau. Jangan lupa panggil \`complete_task\` tepat setelah file ditulis/perintah dijalankan untuk langkah tersebut!
  4. Saat melanjutkan checklist yang sudah ada, JANGAN panggil \`create_plan\` atau \`create_tasks\` lagi; lanjutkan task pending sampai selesai.
- PLAN MODE saat ini: ${planModeEnabled ? 'ON - wajib plan untuk aksi yang mengubah file/menjalankan command.' : 'OFF - jangan pakai create_plan/create_tasks otomatis, langsung eksekusi tool yang diperlukan.'}

GLOBAL CONTEXT LAYERS:
${globalProtocolContext}

REPO MAP (gunakan untuk navigasi kode tanpa cari acak):
${repoMapSummary}`;

const agent = createAgent({
  provider: providerProxy,
  systemPrompt,
  tools: toolRegistry,
  workspaceRoot,
  shouldUseTools: isToolRequest,
  toolNamesForMessage,
  planMode: (message) => message.includes('[VEXALYN_INTERNAL_CONTINUE_NO_PLAN]') ? 'off' : planModeEnabled ? 'on' : 'off',
  approve: async (action, path) => {
    if (tuiAutoFlag) return true;
    const decision = action === 'write'
      ? permissionManager.checkWrite(path)
      : action === 'edit'
        ? permissionManager.checkEdit(path)
        : action === 'delete'
          ? permissionManager.checkDelete(path)
          : permissionManager.checkExecute(path);
    if (decision === 'allowed') return true;
    if (decision === 'denied') return false;
    return new Promise<boolean>((resolve) => {
      const displayAction = action === 'execute' ? 'run command' : action;
      // Stop the loading animation BEFORE writing the permission prompt,
      // otherwise the animation interval overwrites the prompt text.
      stopActiveLoader();
      globalTaskTracker.markOutputInterrupted();
      void writePermissionPrompt(displayAction, path).then(() => {
        permissionResolve = (allowed: boolean) => {
          resolve(allowed);
        };
      });
    });
  },
  ask: async (request) =>
    new Promise<{ answers: string[] }>((resolve) => {
      stopActiveLoader();
      globalTaskTracker.markOutputInterrupted();
      activeQuestion = request;
      void writeQuestionPrompt(request).then(() => {
        askResolve = (result: { answers: string[] }) => {
          activeQuestion = null;
          resolve(result);
        };
      });
    }),
}, logger);

// ── Subagent runner for the `task` tool ──────────────────────────────────────────
// Creates a fresh agent scoped to a focused subagent system prompt. Explore subagents
// get a read-only registry; general subagents share the full registry.
async function runSubagent(prompt: string, type: 'explore' | 'general', description: string): Promise<string> {
  const isExplore = type === 'explore';
  const subRegistry = isExplore
    ? createFilesystemToolRegistry(undefined)
        .register(todoReadTool)
        .register(readFileTool)
        .register(grepTool)
        .register(globTool)
        .register(getProjectInfoTool)
        .register(getProjectStructureTool)
        .register(getCodeOutlineTool)
        .register(semanticSearchTool)
        .register(gitStatusTool)
        .register(gitDiffTool)
        .register(lspTool)
        .register(listDirectoryTool)
        .register(questionTool)
        .register(todoWriteTool)
    : toolRegistry;

  const subSystemPrompt = `${getSubagentPrompt(type)}\n\nWORKSPACE ROOT: ${workspaceRoot}\nSemua path relatif WAJIB dianggap relatif terhadap WORKSPACE ROOT. Jangan mengarang root project.`;

  const subAgent = createAgent({
    provider: providerProxy,
    systemPrompt: subSystemPrompt,
    tools: subRegistry,
    workspaceRoot,
    shouldUseTools: isToolRequest,
    toolNamesForMessage,
    approve: async () => true,
    ask: async () => ({ answers: ['ok'] }),
  }, logger);

  let output = '';
  try {
    for await (const event of subAgent.streamEvents(`[SUBTASK: ${description}]\n${prompt}`)) {
      if (event.type === 'text-delta' && event.text) {
        output += event.text;
      }
    }
  } catch (error) {
    logger.warn(`Subagent ${type} gagal:', error`);
    return `[Subagent error: ${error instanceof Error ? error.message : String(error)}]`;
  }
  return output.trim() || '(subagent tidak mengembalikan output teks)';
}

toolRegistry.register(createTaskTool({ runSubagent }));

function loadHistory() {
  if (!existsSync(historyFilePath)) return;
  try {
    const data = readFileSync(historyFilePath, 'utf8');
    const messages = JSON.parse(data);
    if (Array.isArray(messages)) {
      for (const msg of messages) {
        if (msg && typeof msg === 'object' && (msg.role === 'user' || msg.role === 'assistant')) {
          agent.history.add(msg);
        }
      }
    }
  } catch (error) {
    logger.warn('Gagal memuat history chat:', error);
  }
}

function saveHistory() {
  try {
    const messages = agent.history.list().filter((msg) => msg.role === 'user' || msg.role === 'assistant');
    const dir = resolve(workspaceRoot, '.turbo');
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true });
    }
    writeFileSync(historyFilePath, JSON.stringify(messages, null, 2), 'utf8');

    // Also save to sessions directory with timestamp
    if (messages.length > 0) {
      const sessionsDir = resolve(workspaceRoot, '.vexalyn', 'sessions');
      if (!existsSync(sessionsDir)) {
        mkdirSync(sessionsDir, { recursive: true });
      }
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const sessionFile = resolve(sessionsDir, `session-${timestamp}.json`);
      writeFileSync(sessionFile, JSON.stringify(messages, null, 2), 'utf8');
    }
  } catch (error) {
    logger.warn('Gagal menyimpan history chat:', error);
  }
}

loadHistory();
// Handle TUI start flags ala opencode: --continue, --session, --fork, --prompt, --agent
if (tuiContinueFlag) {
  const sessionsDir = resolve(workspaceRoot, '.vexalyn', 'sessions');
  if (existsSync(sessionsDir)) {
    const files = readdirSync(sessionsDir).filter(f=>f.endsWith('.json')).sort((a,b)=> statSync(resolve(sessionsDir,b)).mtimeMs - statSync(resolve(sessionsDir,a)).mtimeMs);
    if (files[0]) {
      try {
        const data = readFileSync(resolve(sessionsDir, files[0]!), 'utf8');
        const msgs = JSON.parse(data);
        if (Array.isArray(msgs)) { agent.history.clear(); for (const m of msgs) agent.history.add(m); }
        console.log(`\x1b[90m↻ Continued session ${files[0]}\x1b[0m`);
      } catch {}
    }
  }
}
if (tuiSessionFlag) {
  const sf = resolve(workspaceRoot, '.vexalyn', 'sessions', tuiSessionFlag.endsWith('.json') ? tuiSessionFlag : tuiSessionFlag + '.json');
  if (existsSync(sf)) {
    try { const msgs = JSON.parse(readFileSync(sf,'utf8')); if(Array.isArray(msgs)){ agent.history.clear(); for(const m of msgs) agent.history.add(m);} console.log(`\x1b[90m↻ Loaded session ${tuiSessionFlag}\x1b[0m`);} catch{}
  } else console.log(`\x1b[90mSession not found: ${tuiSessionFlag}\x1b[0m`);
}
if (tuiForkFlag) {
  const sf = resolve(workspaceRoot, '.vexalyn', 'sessions', tuiForkFlag.endsWith('.json') ? tuiForkFlag : tuiForkFlag + '.json');
  if (existsSync(sf)) {
    try {
      const msgs = JSON.parse(readFileSync(sf,'utf8'));
      const newId = `session-${Date.now()}.json`;
      writeFileSync(resolve(workspaceRoot, '.vexalyn', 'sessions', newId), JSON.stringify(msgs,null,2));
      agent.history.clear(); for(const m of msgs) if(Array.isArray(msgs)) agent.history.add(m);
      console.log(`\x1b[90m⑂ Forked ${tuiForkFlag} -> ${newId}\x1b[0m`);
    } catch {}
  }
}
if (tuiAgentFlag) {
  console.log(`\x1b[90m🤖 Agent: ${tuiAgentFlag}\x1b[0m`);
  // agent name stored, system prompt already includes it; custom agents loaded from ~/.vexalyn/agents
}
console.log('\x1b[1;92m✔\x1b[0m Agent ready\n');

// ── Undo/Redo stacks ala opencode ───────────────────────────────────────────
const undoStack: Array<Array<{ role: string; content: string }>> = [];
let redoStack: Array<Array<{ role: string; content: string }>> = [];
function snapshotHistory(): Array<{ role: string; content: string }> {
  return agent.history.list().map(m => ({ role: m.role, content: m.content }));
}
function pushUndoSnapshot(): void {
  undoStack.push(snapshotHistory());
  if (undoStack.length > 50) undoStack.shift();
  redoStack = [];
}
let detailsMode = false;
let thinkingMode = false;
let currentTheme = (config as unknown as { theme?: string }).theme ?? 'vexalyn-dark';

// ── Load or ask for username ──────────────────────────────────────────────────
const existingProfile = loadUserProfile();
let username: string;

function isReadlineUsable(): boolean {
  if (readlineClosed) return false;
  const pasteEnded = (pasteInput as unknown as { readableEnded?: boolean }).readableEnded === true;
  const stdinEnded = process.stdin.readableEnded === true;
  return !pasteEnded && !stdinEnded;
}

if (existingProfile) {
  // Profile found — skip prompt, greet directly
  username = existingProfile.name;
} else {
  // No profile — show name prompt card.
  // Empty string = stdin non-interaktif/tertutup: jangan tanya & JANGAN simpan profil junk.
  const promptedName = await new Promise<string>((resolve) => {
    if (!isReadlineUsable()) {
      resolve('');
      return;
    }
    console.log(`${purple}╭──────────────────────────────────────────────╮${reset}`);
    console.log(cardLine(`\x1b[1;95m✦ VEXALYN CODE\x1b[0m`, cWidth));
    console.log(cardLine(`  \x1b[90mSiapa nama kamu?\x1b[0m`, cWidth));
    console.log(cardLine(`  \x1b[90mKetik namamu lalu tekan Enter\x1b[0m`, cWidth));
    console.log(`${purple}╰──────────────────────────────────────────────╯${reset}`);
    console.log('');

    try {
      readline.setPrompt(`${purple}✦${reset} Nama kamu: `);
      readline.prompt();
    } catch {
      resolve('');
      return;
    }

    readline.once('line', (name) => {
      resolve(name.trim());
    });
  });
  if (promptedName) {
    username = promptedName;
    saveUserProfile(username);
  } else {
    username = 'User';
  }
}

// ── Welcome card ──────────────────────────────────────────────────────────────
console.log('');
// Greeting compact ala opencode status bar - single line, tidak makan 5 baris kotak
console.log(`${grey}— halo ${bold}${username}${reset}${grey}, ${existingProfile ? 'welcome back' : 'selamat datang di Vexalyn'} • ${workspaceRoot.split(/[\\/]/).pop() ?? workspaceRoot} • ${activeProviderName}/${activeModelName}${reset}`);

if (isReadlineUsable()) {
  readline.setPrompt(promptText());
  readline.prompt();
  if (tuiPromptFlag) {
    setTimeout(() => { pendingInput = pendingInput.then(() => processLine(tuiPromptFlag)); }, 300);
  }
  // Ctrl+P Quick Open ala opencode - langsung ketik filter file
  try {
    const rlMod = await import('node:readline');
    if (process.stdin.isTTY) {
      rlMod.emitKeypressEvents(process.stdin as unknown as NodeJS.ReadableStream);
      process.stdin.on('keypress', async (_str: string, key: { ctrl?: boolean; name?: string }) => {
        if (!key || !key.ctrl || key.name !== 'p') return;
        if (interactiveCommandActive || permissionResolve || askResolve || planManager.hasPendingPlan()) return;
        // cegah double trigger saat picker sudah aktif
        if ((global as unknown as { __vexalynPickerActive?: boolean }).__vexalynPickerActive) return;
        (global as unknown as { __vexalynPickerActive?: boolean }).__vexalynPickerActive = true;
        try {
          suspendInteractiveInput();
          const picked = await runFilePicker(readline, workspaceRoot);
          if (picked) {
            // Sisipkan path file ke prompt ala opencode (@file) biar user langsung kirim ke agent
            const mention = `@${picked} `;
            // tulis ke line yang sedang aktif
            readline.write(mention);
            process.stdout.write(`\n${grey}› picked ${mention.trim()} - ketik pesan lalu Enter${reset}\n`);
          }
        } finally {
          resumeInteractiveInput();
          if (!readlineClosed) readline.prompt();
          (global as unknown as { __vexalynPickerActive?: boolean }).__vexalynPickerActive = false;
        }
      });
    }
  } catch {}
}
let pendingInput = Promise.resolve();
let interactiveCommandActive = false;
let suppressBufferedModelCommandUntil = 0;

function suspendInteractiveInput(): void {
  process.stdin.unpipe(pasteInput);
  readline.pause();
}

function resumeInteractiveInput(): void {
  if (!readlineClosed) {
    process.stdin.pipe(pasteInput);
    readline.resume();
  }
}

async function runNameChangeCommand(): Promise<void> {
  interactiveCommandActive = true;
  try {
    const nameBoxWidth = Math.max(cWidth, username.length + 19);
    console.log('');
    console.log(boxBorder('top', nameBoxWidth));
    console.log(cardLine(`${bold}${purple}✦ Ganti Nama${reset}`, nameBoxWidth));
    console.log(boxBorder('middle', nameBoxWidth));
    console.log(cardLine(`  \x1b[90mNama sebelumnya\x1b[0m  \x1b[97m${username}\x1b[0m`, nameBoxWidth));
    console.log(cardLine(`  \x1b[90mNama baru\x1b[0m        \x1b[97mketik di bawah\x1b[0m`, nameBoxWidth));
    console.log(boxBorder('bottom', nameBoxWidth));
    console.log('');

    readline.setPrompt(`${purple}✦${reset} Nama baru: `);
    const nextName = await new Promise<string>((resolve) => {
      readline.once('line', (name) => resolve(name.trim()));
      readline.prompt();
    });

    if (!nextName) {
      console.log(`\n${infoBadge} Ganti nama dibatalkan.\n`);
      readline.setPrompt(promptText());
      return;
    }

    saveUserProfile(nextName);
    username = nextName;
    readline.setPrompt(promptText());
    console.log(`\n${infoBadge} Nama diganti. Restart Vexalyn untuk memakai nama baru.\n`);
    readline.close();
} finally {
    interactiveCommandActive = false;
  }
}

async function runNonInteractiveMode(): Promise<void> {
  // Parse arguments: vexalyn run "message" [--model provider/model] [--format json|text]
  const args = process.argv.slice(2);
  const messageIndex = args.findIndex(a => a === 'run');
  if (messageIndex === -1) return;
  
  const messageArgs = args.slice(messageIndex + 1);
  let message = messageArgs.join(' ').trim();
  
  // Parse flags
  let format: 'json' | 'text' = 'text';
  let modelOverride: string | undefined;
  
  for (let i = 0; i < messageArgs.length; i++) {
    if (messageArgs[i] === '--format' && i + 1 < messageArgs.length) {
      format = messageArgs[i + 1] as 'json' | 'text';
      i++;
    } else if (messageArgs[i] === '-f' && i + 1 < messageArgs.length) {
      format = messageArgs[i + 1] as 'json' | 'text';
      i++;
    } else if (messageArgs[i] === '--model' && i + 1 < messageArgs.length) {
      modelOverride = messageArgs[i + 1];
      i++;
    } else if (messageArgs[i] === '-m' && i + 1 < messageArgs.length) {
      modelOverride = messageArgs[i + 1];
      i++;
    }
  }
  
  // Reconstruct message without flags
  const messageParts = messageArgs.filter((arg, i) => {
    if (arg === '--format' || arg === '-f' || arg === '--model' || arg === '-m') return false;
    if (i > 0 && (messageArgs[i - 1] === '--format' || messageArgs[i - 1] === '-f' || messageArgs[i - 1] === '--model' || messageArgs[i - 1] === '-m')) return false;
    return true;
  });
  message = messageParts.join(' ').trim();
  
  if (!message) {
    console.error('Usage: vexalyn run "your message" [--model provider/model] [--format json|text]');
    process.exit(1);
  }

  // Initialize everything needed for non-interactive mode
  loadEnvFiles(workspaceRoot, import.meta.url);
  
  const PROVIDER_FALLBACK_ORDER: ProviderName[] = [
    'qwen', 'openai', 'gemini', 'anthropic', 'deepseek', 'groq', 'openrouter',
    'zai', 'moonshot', 'mistral', 'xai', 'cerebras', 'together', 'fireworks',
    'deepinfra', 'siliconflow', 'nvidia', 'cohere', 'perplexity', 'sambanova',
    'novita', 'ai21', 'ollama',
  ];
  if (!process.env.VEXALYN_DEFAULT_PROVIDER) {
    process.env.VEXALYN_DEFAULT_PROVIDER = PROVIDER_FALLBACK_ORDER.find(hasConfiguredProvider) ?? 'qwen';
  }
  if (!process.env.VEXALYN_DEFAULT_MODEL) {
    const fallbackModels = DEFAULT_PROVIDER_MODELS[process.env.VEXALYN_DEFAULT_PROVIDER as ProviderName];
    process.env.VEXALYN_DEFAULT_MODEL = fallbackModels?.[0] ?? 'gpt-4o-mini';
  }

  const config = loadConfig(process.env, workspaceRoot);
  const logger = createLogger(config.logLevel);
  let provider = initActiveProvider(config);
  if (modelOverride) {
    try {
      const modelName = modelOverride.split('/').pop() ?? 'gpt-4o-mini';
      provider = createProvider({ ...config, defaultModel: modelName });
    } catch {
      console.error('Failed to create provider with model:', modelOverride);
      process.exit(1);
    }
  }

  const toolRegistry = createFilesystemToolRegistry(undefined)
    .register(createTasksTool)
    .register(completeTaskTool);

  // Build system prompt (simplified for non-interactive)
  const globalProtocolContext = `Gunakan tools yang tersedia untuk menyelesaikan tugas. Jangan mengarang file atau folder.`;
  const repoMapSummary = '';
  const systemPrompt = `Kamu adalah VEXALYN CODE, coding agent lokal yang berjalan langsung di komputer user.

WORKSPACE ROOT: ${workspaceRoot}
Semua path relatif WAJIB dianggap relatif terhadap WORKSPACE ROOT.

${globalProtocolContext}

REPO MAP (gunakan untuk navigasi kode tanpa cari acak):
${repoMapSummary}

ATURAN:
- Gunakan tool filesystem untuk workspace aktif.
- Jangan mengarang root project atau hanya menampilkan contoh; kerjakan perubahan yang diminta.
- Baca file dengan read_file, BUKAN run_command shell.
- Untuk pencarian kode: gunakan grep (regex) atau glob (pattern file) lalu read_file.
- Untuk pencarian file: gunakan glob (pattern) atau list_directory.

TOOLS: read_file, write_file, edit_file, apply_patch, delete_file, create_directory, delete_directory, list_directory, search_files, grep, glob, run_command, web_search, fetch_web_page, semantic_search, get_code_outline, git_status, git_diff, question, lsp, skill, create_plan, create_tasks, complete_task, todowrite, todoread, task.`;

  // Create agent without interactive features
  const agent = createAgent({
    provider,
    systemPrompt,
    tools: toolRegistry,
    workspaceRoot,
    shouldUseTools: isToolRequest,
    toolNamesForMessage,
    approve: async () => true,
    ask: async () => ({ answers: ['ok'] }),
  }, logger);

  let output = '';
  const toolCalls: Array<{ name: string; input: unknown; result: unknown }> = [];
  
  try {
    for await (const event of agent.streamEvents(message)) {
      if (event.type === 'text-delta' && event.text) {
        output += event.text;
      } else if (event.type === 'tool-call') {
        toolCalls.push({ name: event.name ?? '', input: event.input, result: undefined });
      } else if (event.type === 'tool-result') {
        const lastCall = toolCalls[toolCalls.length - 1];
        if (lastCall) lastCall.result = event.result;
      }
    }
  } catch (error) {
    console.error('Error:', error instanceof Error ? error.message : String(error));
    process.exit(1);
  }

  if (format === 'json') {
    console.log(JSON.stringify({ output: output.trim(), toolCalls }, null, 2));
  } else {
    console.log(output.trim());
  }
}

async function runInitCommand(workspaceRoot: string): Promise<void> {
  interactiveCommandActive = true;
  suspendInteractiveInput();
  try {
    const agentsMdPath = resolve(workspaceRoot, 'AGENTS.md');
    if (existsSync(agentsMdPath)) {
      console.log(`\n${infoBadge} AGENTS.md sudah ada di ${agentsMdPath}`);
      readline.setPrompt(`${purple}✦${reset} Timpa file? (y/N): `);
      const answer = await new Promise<string>((resolve) => {
        readline.once('line', (line) => resolve(line.trim().toLowerCase()));
        readline.prompt();
      });
      if (answer !== 'y' && answer !== 'yes') {
        console.log(`${infoBadge} Dibatalkan.\n`);
        return;
      }
    }

    const template = `# AGENTS.md
# Instruksi untuk VEXALYN CODE di project ini

## Project Overview
<!-- Deskripsi singkat project, tech stack, arsitektur -->

## Coding Standards
<!-- Style guide, naming conventions, best practices -->

## Commands
<!-- build, test, lint, dev, deploy commands -->

## Architecture Notes
<!-- Key architectural decisions, patterns used -->

## Important Files
<!-- File kunci yang perlu diketahui agent -->

## Environment
<!-- Environment variables, setup instructions -->

## Testing
<!-- Test commands, coverage requirements -->

## Git Workflow
<!-- Branch naming, commit conventions, PR process -->
`;

    writeFileSync(agentsMdPath, template, 'utf8');
    console.log(`\n${infoBadge} AGENTS.md dibuat di ${agentsMdPath}`);
    console.log(`${infoBadge} Edit file ini untuk menambahkan instruksi khusus project.\n`);
  } finally {
    resumeInteractiveInput();
    interactiveCommandActive = false;
  }
}

async function runResumeCommand(
  readline: ReturnType<typeof createInterface>,
  agent: ReturnType<typeof createAgent>,
): Promise<void> {
  interactiveCommandActive = true;
  suspendInteractiveInput();
  try {
    const sessionsDir = resolve(workspaceRoot, '.vexalyn', 'sessions');
    if (!existsSync(sessionsDir)) {
      console.log(`\n${infoBadge} Tidak ada sesi tersimpan.\n`);
      return;
    }

    const sessionFiles = readdirSync(sessionsDir)
      .filter(f => f.endsWith('.json'))
      .sort((a, b) => {
        const statA = statSync(resolve(sessionsDir, a));
        const statB = statSync(resolve(sessionsDir, b));
        return statB.mtimeMs - statA.mtimeMs;
      });

    if (sessionFiles.length === 0) {
      console.log(`\n${infoBadge} Tidak ada sesi tersimpan.\n`);
      return;
    }

    console.log('\nSesi tersedia:');
    sessionFiles.forEach((file, i) => {
      const stat = statSync(resolve(sessionsDir, file));
      const size = (stat.size / 1024).toFixed(1);
      console.log(`  ${i + 1}. ${file} (${size} KB, ${stat.mtime.toLocaleString()})`);
    });

    readline.setPrompt(`${purple}✦${reset} Pilih nomor sesi (atau Enter untuk batal): `);
    const input = await new Promise<string>((resolve) => {
      readline.once('line', (line) => resolve(line.trim()));
      readline.prompt();
    });

    if (!input) {
      console.log(`${infoBadge} Dibatalkan.\n`);
      return;
    }

    const index = parseInt(input, 10) - 1;
    if (isNaN(index) || index < 0 || index >= sessionFiles.length) {
      console.log(`${infoBadge} Nomor tidak valid.\n`);
      return;
    }

    const sessionFileName = sessionFiles[index];
    if (!sessionFileName) {
      console.log(`${infoBadge} Nomor tidak valid.\n`);
      return;
    }
    const sessionFile = resolve(sessionsDir, sessionFileName);
    try {
      const data = readFileSync(sessionFile, 'utf8');
      const messages = JSON.parse(data);
      if (Array.isArray(messages)) {
        agent.history.clear();
        for (const msg of messages) {
          if (msg && typeof msg === 'object' && (msg.role === 'user' || msg.role === 'assistant' || msg.role === 'system')) {
            agent.history.add(msg);
}
}
        console.log(`\n${infoBadge} Sesi "${sessionFiles[index]}" dilanjutkan (${messages.length} pesan).\n`);
      }
    } catch (error) {
      console.log(`\n${infoBadge} Gagal memuat sesi: ${error instanceof Error ? error.message : String(error)}\n`);
    }
  } finally {
    resumeInteractiveInput();
    interactiveCommandActive = false;
  }
}

async function continuePendingTasks(): Promise<void> {
  let attempts = 0;
  let previousDoneCount = globalTaskTracker.doneCount;
  const maxAttempts = Math.min(6, Math.max(3, globalTaskTracker.remainingCount));

  while (globalTaskTracker.hasPending() && attempts < maxAttempts) {
    attempts++;
    await writeAgentEventResponse(
      agent,
      [
        '[VEXALYN_INTERNAL_CONTINUE_NO_PLAN]',
        'Lanjutkan task project yang belum selesai dari permintaan sebelumnya.',
        'Jangan panggil create_plan/create_tasks lagi dan jangan ulangi file yang sudah selesai.',
        'Buat atau edit file yang masih kurang, jalankan verifikasi bila diperlukan, lalu panggil complete_task untuk task yang selesai.',
      ].join(' '),
    );

    if (globalTaskTracker.doneCount <= previousDoneCount) break;
    previousDoneCount = globalTaskTracker.doneCount;
  }
}

async function processLine(line: string): Promise<void> {
  const input = line.trim();
  if (!input) { readline.prompt(); return; }
  // Clear the echoed input line for ALL inputs
  clearSubmittedInput(line);

  globalTaskTracker.setTasks([]); // reset task tracker for a new request

  if (input === 'exit' || input === 'quit' || input === '/exit' || input === '/quit' || input === '/q' || input === 'q') {
    console.log(`\n${infoBadge} VEXALYN CODE pamit dulu, sampai ketemu lagi!`);
    readline.close();
    return;
  }
  if (input === 'clear' || input === '/clear') {
    agent.history.clear();
    try {
      if (existsSync(historyFilePath)) {
        writeFileSync(historyFilePath, '[]', 'utf8');
      }
    } catch { }
    console.log('History cleared.');
    readline.prompt();
    return;
  }
  if (input === '/new') {
    // Full session reset: clear history, task tracker, todo list, and context
    agent.history.clear();
    globalTaskTracker.setTasks([]);
    clearTodos();
    try {
      if (existsSync(historyFilePath)) {
        writeFileSync(historyFilePath, '[]', 'utf8');
      }
    } catch { }
    console.log('New session started. History and state cleared.');
    readline.prompt();
    return;
  }
  if (input === '/init') {
    await runInitCommand(workspaceRoot);
    readline.prompt();
    return;
  }
  if (input === '/resume') {
    await runResumeCommand(readline, agent);
    readline.prompt();
    return;
  }
  if (input === '/hapus akun') {
    deleteUserProfile();
    console.log(`\n${infoBadge} Akun dihapus. Restart Vexalyn untuk memasukkan nama baru.\n`);
    readline.close();
    return;
  }
  if (input === '/ganti nama') {
    await runNameChangeCommand();
    return;
  }
  if (input === '/help') {
    runHelpCommand();
    readline.prompt();
    return;
  }
  if (input === '/history') {
    runHistoryCommand(agent);
    readline.prompt();
    return;
  }
  if (input === '/compact') {
    await runCompactCommand(agent, activeProvider);
    readline.prompt();
    return;
  }
  if (input === '/plan') {
    interactiveCommandActive = true;
    suspendInteractiveInput();
    try {
      const result = await runPlanCommand(readline, planModeEnabled);
      if (result !== null) {
        planModeEnabled = result;
        settings.planModeEnabled = result;
        saveUserSettings(settings);
      }
    } finally {
      resumeInteractiveInput();
      interactiveCommandActive = false;
    }
    if (!readlineClosed) readline.prompt();
    return;
  }
  if (input === '/usage') {
    runUsageCommand(getUsageInfo(systemPrompt));
    readline.prompt();
    return;
  }
  if (input === '/model' || input === '/models') {
    interactiveCommandActive = true;
    suspendInteractiveInput();
    try {
      const result = await runModelCommand(readline, activeProviderName, activeModelName);
      if (result) {
        activeProvider     = result.newProvider;
        activeProviderName = result.provider;
        activeModelName    = result.model;
      }
    } finally {
      suppressBufferedModelCommandUntil = Date.now() + 500;
      resumeInteractiveInput();
      interactiveCommandActive = false;
    }
    if (!readlineClosed) readline.prompt();
    return;
  }
  if (input === '/stats' || input === '/usage') {
    // /stats ganti /usage, tapi /usage tetap alias
    runUsageCommand(getUsageInfo(systemPrompt));
    readline.prompt();
    return;
  }
  if (input === '/summarize') {
    await runCompactCommand(agent, activeProvider);
    readline.prompt();
    return;
  }
  if (input === '/details') {
    detailsMode = !detailsMode;
    console.log(`\n${infoBadge} Details mode: ${detailsMode ? 'ON — tool detail verbose' : 'OFF — ringkas'}\n`);
    readline.prompt();
    return;
  }
  if (input === '/editor') {
    try {
      const editor = process.env.EDITOR || process.env.VISUAL || 'notepad';
      const tmpFile = resolve(workspaceRoot, '.turbo/vexalyn-editor-tmp.md');
      if (!existsSync(resolve(workspaceRoot, '.turbo'))) mkdirSync(resolve(workspaceRoot, '.turbo'), { recursive: true });
      writeFileSync(tmpFile, input, 'utf8');
      const { spawnSync } = await import('node:child_process');
      spawnSync(editor, [tmpFile], { stdio: 'inherit' });
      const edited = readFileSync(tmpFile, 'utf8');
      if (edited.trim() && edited.trim() !== input) {
        pushUndoSnapshot();
        try { await mapPromise; } catch {}
        await writeAgentEventResponse(agent, edited);
        await continuePendingTasks();
        globalTaskTracker.summarize();
        saveHistory();
      }
    } catch (e) { console.error('Editor gagal: ' + (e instanceof Error ? e.message : String(e))); }
    readline.prompt();
    return;
  }
  if (input === '/export') {
    const outFile = resolve(workspaceRoot, `vexalyn-export-${new Date().toISOString().slice(0,10)}.md`);
    const msgs = agent.history.list().filter(m => m.role === 'user' || m.role === 'assistant');
    const md = msgs.map(m => `## ${m.role}\n${m.content}`).join('\n\n---\n\n');
    writeFileSync(outFile, `# Vexalyn Export ${new Date().toISOString()}\n\n${md}`, 'utf8');
    console.log(`\n${infoBadge} Export ke ${outFile} (${msgs.length} pesan)\n`);
    readline.prompt();
    return;
  }
  if (input === '/connect' || input.startsWith('/connect ')) {
    const prov = input.split(' ')[1] ?? '';
    if (!prov) { console.log('\nUsage: /connect <provider>  (contoh: /connect openai)\n'); readline.prompt(); return; }
    console.log(`\n${infoBadge} Gunakan: vexalyn auth login ${prov} lalu restart.\n`);
    readline.prompt();
    return;
  }
  if (input === '/sessions') {
    const sessionsDir = resolve(workspaceRoot, '.vexalyn', 'sessions');
    if (!existsSync(sessionsDir)) { console.log(`\n${infoBadge} Tidak ada sesi.\n`); readline.prompt(); return; }
    const files = readdirSync(sessionsDir).filter(f => f.endsWith('.json')).sort((a,b)=> statSync(resolve(sessionsDir,b)).mtimeMs - statSync(resolve(sessionsDir,a)).mtimeMs);
    if (files.length===0) console.log(`\n${infoBadge} Tidak ada sesi.\n`);
    else { console.log('\nSessions:'); files.forEach((f,i)=> console.log(`  ${i+1}. ${f}`)); console.log(''); }
    readline.prompt();
    return;
  }
  if (input === '/continue') {
    if (globalTaskTracker.hasPending()) { await continuePendingTasks(); saveHistory(); }
    else console.log(`\n${infoBadge} Tidak ada task pending untuk dilanjutkan.\n`);
    readline.prompt();
    return;
  }
  if (input === '/share') {
    console.log(`\n${infoBadge} Share session (stub) — upload ke vexalyn cloud, link: https://share.vexalyn.dev/${Date.now().toString(36)}\n`);
    readline.prompt();
    return;
  }
  if (input === '/unshare') {
    console.log(`\n${infoBadge} Unshare session (stub) — share link dihapus.\n`);
    readline.prompt();
    return;
  }
  if (input === '/themes') {
    const themes = ['vexalyn-dark','vexalyn-light','opencode-dark','opencode-light','monokai','dracula','nord','tokyo-night','catppuccin','solarized-dark','solarized-light','github-dark','github-light'];
    interactiveCommandActive = true;
    suspendInteractiveInput();
    try {
      const { runThemePicker } = await import('./commands.js');
      if (runThemePicker) {
        const picked = await (runThemePicker as unknown as (rl: unknown, cur: string, list: string[]) => Promise<string | null>)(readline, currentTheme, themes);
        if (picked) { currentTheme = picked; (config as unknown as { theme?: string }).theme = picked; console.log(`\n${infoBadge} Theme: ${picked}\n`); }
      } else {
        console.log('\nThemes: ' + themes.join(', ') + '\n');
      }
    } catch { console.log('\nThemes: ' + themes.join(', ') + '\n'); }
    finally { resumeInteractiveInput(); interactiveCommandActive = false; }
    if (!readlineClosed) readline.prompt();
    return;
  }
  if (input === '/thinking') {
    thinkingMode = !thinkingMode;
    console.log(`\n${infoBadge} Thinking mode: ${thinkingMode ? 'ON — tampilkan reasoning' : 'OFF'}\n`);
    readline.prompt();
    return;
  }
  if (input === '/undo') {
    if (undoStack.length === 0) { console.log(`\n${infoBadge} Tidak ada yang bisa di-undo.\n`); readline.prompt(); return; }
    const snap = undoStack.pop()!;
    redoStack.push(snapshotHistory());
    agent.history.clear();
    for (const m of snap) agent.history.add(m as { role: 'user' | 'assistant' | 'system'; content: string });
    saveHistory();
    console.log(`\n${infoBadge} Undo — kembali ke ${snap.length} pesan.\n`);
    readline.prompt();
    return;
  }
  if (input === '/redo') {
    if (redoStack.length === 0) { console.log(`\n${infoBadge} Tidak ada yang bisa di-redo.\n`); readline.prompt(); return; }
    const snap = redoStack.pop()!;
    undoStack.push(snapshotHistory());
    agent.history.clear();
    for (const m of snap) agent.history.add(m as { role: 'user' | 'assistant' | 'system'; content: string });
    saveHistory();
    console.log(`\n${infoBadge} Redo — maju ke ${snap.length} pesan.\n`);
    readline.prompt();
    return;
  }

  try {
    // Pastikan repo map background sudah selesai sebelum kirim ke model (prompt sudah bisa diketik dari awal)
    try { await mapPromise; } catch {}
    pushUndoSnapshot();
    await writeAgentEventResponse(agent, input);
    await continuePendingTasks();
    globalTaskTracker.summarize();
    saveHistory();
  } catch (error) {
    console.error(`Error: ${error instanceof Error ? error.message : String(error)}`);
  }
  if (!readlineClosed) readline.prompt();
}

readline.on('line', (line) => {
  if (interactiveCommandActive) return;
  if (Date.now() < suppressBufferedModelCommandUntil && line.trim() === '/model') return;
  if (permissionResolve) {
    const resolve = permissionResolve;
    permissionResolve = null;
    clearSubmittedInput(line);
    const answer = line.trim().toLowerCase();
    resolve(answer === 'y');
    return;
  }
  if (askResolve && activeQuestion) {
    const resolve = askResolve;
    askResolve = null;
    clearSubmittedInput(line);
    const raw = line.trim();
    if (!raw) {
      resolve({ answers: [] });
      return;
    }
    const options = activeQuestion.options ?? [];
    const numericTokens = raw.split(/[,\s]+/).filter(Boolean);
    const allNumeric = numericTokens.length > 0 && numericTokens.every((token) => /^\d+$/.test(token));
    if (options.length > 0 && allNumeric) {
      const picked = numericTokens
        .map((token) => Number.parseInt(token, 10))
        .filter((index) => index >= 1 && index <= options.length)
        .map((index) => options[index - 1]!);
      const valid = activeQuestion.multiple ? picked : picked.slice(0, 1);
      if (valid.length === numericTokens.length || (valid.length > 0 && !activeQuestion.multiple)) {
        resolve({ answers: [...new Set(valid)] });
        return;
      }
      resolve({ answers: [raw] });
      return;
    }
    resolve({ answers: [raw] });
    return;
  }
  if (planManager.hasPendingPlan()) {
    const answer = line.trim().toLowerCase();
    clearSubmittedInput(line);
    planManager.resolve(answer === 'y');
    return;
  }
  pendingInput = pendingInput.then(() => processLine(line));
});
readline.on('close', () => {
  readlineClosed = true;
  process.stdout.write('\x1b[?2004l');
  setImmediate(() => {
    pendingInput.then(() => process.exit(0), () => process.exit(1));
  });
});
